/**
 * Raw Axios wrappers for the Admin endpoints.
 *
 * Uses the one shared `apiClient`, so the admin panel inherits the same bearer
 * attach, proactive refresh and single 401 retry as the user-facing apps.
 */
import type { AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import ADMIN_ENDPOINTS from './adminEndpoints';
import type {
  AdminAccountActiveStatusRequest,
  AdminAccountCreateRequest,
  AdminAccountListRequest,
  AdminAccountUpdateRequest,
  AdminBulkUpdateReportedPostRequest,
  AdminReportedPostsFetchRequest,
  AdminUserActiveStatusRequest,
  AdminUserDetailsResponse,
  AdminUserDetailsResponsePagedResult,
  AdminUserDetailsWrapperResponse,
  AdminUserListRequest,
  AdminUserOperationResponse,
  AdminUserStatisticsListRequest,
  AdminUserStatisticsListResponse,
  AdminUserUpdateRequest,
  BulkUpdatePostRequest,
  DashboardMetricsRequest,
  DashboardNew,
  ErrorLogListRequest,
  ErrorLogResponsePagedResult,
  SetTemporaryPasswordRequest,
} from './adminTypes';

const adminApi = {
  // ─── Users ─────────────────────────────────────────────────────────────────

  listUsers: (
    payload: AdminUserListRequest,
  ): Promise<AxiosResponse<AdminUserDetailsResponsePagedResult>> =>
    apiClient.post(ADMIN_ENDPOINTS.USER.LIST, payload),

  getUser: (
    userGuid: string,
  ): Promise<AxiosResponse<AdminUserDetailsWrapperResponse>> =>
    apiClient.get(ADMIN_ENDPOINTS.USER.BY_GUID(userGuid)),

  updateUser: (
    userGuid: string,
    payload: AdminUserUpdateRequest,
  ): Promise<AxiosResponse<AdminUserOperationResponse>> =>
    apiClient.put(ADMIN_ENDPOINTS.USER.BY_GUID(userGuid), payload),

  setUserActiveStatus: (
    payload: AdminUserActiveStatusRequest,
  ): Promise<AxiosResponse<AdminUserOperationResponse>> =>
    apiClient.post(ADMIN_ENDPOINTS.USER.ACTIVE_STATUS, payload),

  setTemporaryPassword: (
    payload: SetTemporaryPasswordRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(ADMIN_ENDPOINTS.USER.SET_TEMPORARY_PASSWORD, payload),

  listUserStatistics: (
    payload: AdminUserStatisticsListRequest,
  ): Promise<AxiosResponse<AdminUserStatisticsListResponse>> =>
    apiClient.post(ADMIN_ENDPOINTS.USER.STATISTICS, payload),

  // ─── Admin accounts ────────────────────────────────────────────────────────

  listAdminAccounts: (
    payload: AdminAccountListRequest,
  ): Promise<AxiosResponse<AdminUserDetailsResponsePagedResult>> =>
    apiClient.post(ADMIN_ENDPOINTS.ACCOUNT.LIST, payload),

  createAdminAccount: (
    payload: AdminAccountCreateRequest,
  ): Promise<AxiosResponse<AdminUserOperationResponse>> =>
    apiClient.post(ADMIN_ENDPOINTS.ACCOUNT.CREATE, payload),

  getAdminAccount: (
    userGuid: string,
  ): Promise<AxiosResponse<AdminUserDetailsResponse>> =>
    apiClient.get(ADMIN_ENDPOINTS.ACCOUNT.BY_GUID(userGuid)),

  updateAdminAccount: (
    userGuid: string,
    payload: AdminAccountUpdateRequest,
  ): Promise<AxiosResponse<AdminUserOperationResponse>> =>
    apiClient.put(ADMIN_ENDPOINTS.ACCOUNT.BY_GUID(userGuid), payload),

  setAdminAccountActiveStatus: (
    payload: AdminAccountActiveStatusRequest,
  ): Promise<AxiosResponse<AdminUserOperationResponse>> =>
    apiClient.post(ADMIN_ENDPOINTS.ACCOUNT.ACTIVE_STATUS, payload),

  // ─── Dashboard & moderation ────────────────────────────────────────────────

  getDashboardMetrics: (
    payload: DashboardMetricsRequest,
  ): Promise<AxiosResponse<DashboardNew>> =>
    apiClient.post(ADMIN_ENDPOINTS.DASHBOARD.METRICS, payload),

  getReportedPosts: (
    payload: AdminReportedPostsFetchRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(ADMIN_ENDPOINTS.DASHBOARD.REPORTED_POSTS, payload),

  bulkUpdateReportedContent: (
    payload: AdminBulkUpdateReportedPostRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(ADMIN_ENDPOINTS.DASHBOARD.BULK_UPDATE_REPORTED, payload),

  bulkUpdatePostVisibility: (
    payload: BulkUpdatePostRequest,
  ): Promise<AxiosResponse<unknown>> =>
    apiClient.post(ADMIN_ENDPOINTS.DASHBOARD.BULK_UPDATE_POST, payload),

  // ─── Error log ─────────────────────────────────────────────────────────────

  listErrorLogs: (
    payload: ErrorLogListRequest,
  ): Promise<AxiosResponse<ErrorLogResponsePagedResult>> =>
    apiClient.post(ADMIN_ENDPOINTS.ERROR_LOG.LIST, payload),
};

export default adminApi;
